import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Download,
  Edit2,
  Crown,
  Star,
  Users,
  Gem,
  Calendar,
  Zap,
  FileText,
  Coins,
  Clock,
  Filter,
} from 'lucide-react';
import type { DashboardUser } from '@/lib/dashboard-service';
import { formatDistanceToNow } from 'date-fns';
import { getCountryName } from '@/utils/countryUtils';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';

interface EnhancedUserTableProps {
  users: DashboardUser[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  searchQuery: string;
  roleFilter: string;
  subscriptionFilter: string;
  activityFilter: string;
  onSearchChange: (query: string) => void;
  onRoleFilterChange: (role: string) => void;
  onSubscriptionFilterChange: (subscription: string) => void;
  onActivityFilterChange: (activity: string) => void;
  onPageChange: (page: number) => void;
  onExport: () => void;
  onManageUser: (user: DashboardUser) => void;
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (open: boolean) => void;
}

// Convert a 2-letter country code (ISO-3166 alpha-2) into its flag emoji.
// Returns empty string when the code is not usable (defensive — admin data may
// have pre-validation entries).
const countryToFlagEmoji = (code?: string | null): string => {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  // Regional indicator offset: 'A' = 0x1F1E6.
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (cc.charCodeAt(0) - 65), A + (cc.charCodeAt(1) - 65));
};

type ActivityTier = {
  dot: string; // tailwind bg-* color for the status dot
  text: string; // tailwind text-* color for the label
  label: string;
  pulse?: boolean;
};

// Compute activity tier from the (now accurate) lastActiveAt timestamp.
// Thresholds are tuned for a recipe app where users don't necessarily visit
// daily: <1h Active now, <24h today, <7d this week, <30d this month, else inactive.
const getActivityTier = (lastActiveAt: Date, now: number = Date.now()): ActivityTier => {
  const ms = now - lastActiveAt.getTime();
  const ONE_H = 3_600_000;
  const ONE_D = ONE_H * 24;
  const SEVEN_D = ONE_D * 7;
  const THIRTY_D = ONE_D * 30;

  if (ms < ONE_H) return { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Active now', pulse: true };
  if (ms < ONE_D) return { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Today' };
  if (ms < SEVEN_D) return { dot: 'bg-sky-500', text: 'text-sky-600', label: 'This week' };
  if (ms < THIRTY_D) return { dot: 'bg-amber-500', text: 'text-amber-600', label: 'This month' };
  return { dot: 'bg-gray-400', text: 'text-gray-500', label: 'Inactive' };
};

// Short absolute date label shown below the relative time so admins can pin
// the exact last-accessed date without needing a tooltip.
const formatShortDate = (d: Date): string =>
  d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatNumber = (n: number): string => (Number.isFinite(n) ? n.toLocaleString() : '0');

const EnhancedUserTable: React.FC<EnhancedUserTableProps> = ({
  users,
  totalCount,
  currentPage,
  pageSize,
  isLoading = false,
  searchQuery,
  roleFilter,
  subscriptionFilter,
  activityFilter,
  onSearchChange,
  onRoleFilterChange,
  onSubscriptionFilterChange,
  onActivityFilterChange,
  onPageChange,
  onExport,
  onManageUser,
  mobileFiltersOpen,
  setMobileFiltersOpen,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const hasActiveFilters =
    !!searchQuery || roleFilter !== 'all' || subscriptionFilter !== 'all' || activityFilter !== 'all';

  const initialsFor = (u: DashboardUser): string => {
    const base = (u.name && u.name.trim()) || u.email || '';
    const parts = base.split(/[\s@._-]+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  // Pre-compute per-user activity tier once per render — cheap and keeps the
  // render tree free of inline math.
  const decorated = useMemo(() => users.map(u => ({
    u,
    tier: getActivityTier(u.lastActiveAt),
    flag: countryToFlagEmoji(u.country),
    countryName: getCountryName(u.country),
  })), [users]);

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-yellow-500 text-white"><Crown className="h-3 w-3 mr-1" />Admin</Badge>;
      case 'reader':
        return <Badge variant="default"><Star className="h-3 w-3 mr-1" />Reader</Badge>;
      case 'guest':
        return <Badge variant="outline"><Users className="h-3 w-3 mr-1" />Guest</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getSubscriptionBadge = (subscription: string) => {
    switch (subscription) {
      case 'premium':
        return <Badge className="bg-purple-500 text-white"><Gem className="h-3 w-3 mr-1" />Premium</Badge>;
      case 'standard':
        return <Badge variant="secondary"><Star className="h-3 w-3 mr-1" />Standard</Badge>;
      case 'free':
        return <Badge variant="outline">Free</Badge>;
      case 'banned':
        return <Badge variant="destructive">Banned</Badge>;
      default:
        return <Badge variant="secondary">{subscription}</Badge>;
    }
  };

  // Shared "Last Accessed" cell — relative time + status dot + absolute date.
  const LastAccessed = ({ u, tier }: { u: DashboardUser; tier: ActivityTier }) => (
    <div className="flex items-start gap-2 min-w-[150px]">
      <span
        className={`mt-1 h-2 w-2 shrink-0 rounded-full ${tier.dot}${tier.pulse ? ' animate-pulse' : ''}`}
        aria-hidden="true"
      />
      <div className="flex flex-col">
        <span className={`text-sm font-medium ${tier.text}`}>
          {tier.label === 'Active now'
            ? 'Active now'
            : formatDistanceToNow(u.lastActiveAt, { addSuffix: true })}
        </span>
        <span className="text-[11px] text-gray-400 dark:text-gray-500" title={formatShortDate(u.lastActiveAt)}>
          {formatShortDate(u.lastActiveAt)}
        </span>
      </div>
    </div>
  );

  // ChefCoin cell: balance + lifetime earned/spent + last transaction hint.
  const ChefCoinCell = ({ u }: { u: DashboardUser }) => {
    const hasTxn = u.transactionsCount > 0;
    return (
      <div className="flex flex-col gap-0.5 min-w-[140px]">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <ChefCoinIcon className="h-3.5 w-3.5 text-yellow-500" />
          {formatNumber(u.chefcoins_balance)}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400" title="Total earned via grants/rewards">
            ↑{formatNumber(u.coinsEarned)}
          </span>
          <span className="inline-flex items-center gap-0.5 text-rose-600 dark:text-rose-400" title="Total spent on AI features">
            ↓{formatNumber(u.coinsSpent)}
          </span>
        </div>
        <div className="text-[10px] text-gray-400 dark:text-gray-500">
          {hasTxn
            ? `${u.transactionsCount} txn · last ${formatDistanceToNow(u.lastTransactionAt!, { addSuffix: true })}`
            : 'no transactions'}
        </div>
      </div>
    );
  };

  // Compact usage cell bundling studio uses, AI tokens, recipe count.
  const UsageCell = ({ u }: { u: DashboardUser }) => (
    <div className="flex flex-col gap-1 min-w-[150px]">
      <div className="flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1" title="Studio generations">
          <Zap className="h-3 w-3 text-orange-500" />
          <span className="font-medium">{formatNumber(u.studioUsesCount)}</span>
        </span>
        <span className="inline-flex items-center gap-1" title="AI tokens used">
          <Gem className="h-3 w-3 text-purple-500" />
          <span className="font-medium">{formatNumber(u.aiTokensUsed)}</span>
        </span>
        <span className="inline-flex items-center gap-1" title="Recipes created">
          <FileText className="h-3 w-3 text-blue-500" />
          <span className="font-medium">{formatNumber(u.recipesCreated)}</span>
        </span>
      </div>
    </div>
  );

  const UserCell = ({ u, flag, countryName }: { u: DashboardUser; flag: string; countryName: string }) => (
    <div className="flex items-center gap-2.5 min-w-[200px]">
      <div className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-xs flex items-center justify-center font-semibold uppercase">
        {initialsFor(u)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate" title={u.email}>{u.email}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 truncate">
          {u.name && <span className="truncate">{u.name}</span>}
          {u.name && (u.country || countryName) && <span className="text-gray-300">·</span>}
          {flag && <span aria-hidden="true">{flag}</span>}
          {(u.country || countryName) && (
            <span className="truncate">{countryName || u.country}</span>
          )}
        </div>
      </div>
    </div>
  );

  const JoinedCell = ({ u }: { u: DashboardUser }) => (
    <div className="flex flex-col min-w-[120px]">
      <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}
      </span>
      <span className="text-[10px] text-gray-400">{u.daysActive} days active</span>
    </div>
  );

  // Filter controls reused by desktop toolbar and mobile drawer.
  const Filters = ({ idPrefix }: { idPrefix: string }) => (
    <>
      <Input
        id={`${idPrefix}-search`}
        placeholder="Search by email or name"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full sm:w-64"
      />
      <Select value={roleFilter} onValueChange={onRoleFilterChange}>
        <SelectTrigger id={`${idPrefix}-role`} className="w-full sm:w-32">
          <SelectValue placeholder="Role" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Roles</SelectItem>
          <SelectItem value="admin">Admin</SelectItem>
          <SelectItem value="reader">Reader</SelectItem>
          <SelectItem value="guest">Guest</SelectItem>
        </SelectContent>
      </Select>
      <Select value={subscriptionFilter} onValueChange={onSubscriptionFilterChange}>
        <SelectTrigger id={`${idPrefix}-sub`} className="w-full sm:w-36">
          <SelectValue placeholder="Subscription" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Tiers</SelectItem>
          <SelectItem value="premium">Premium</SelectItem>
          <SelectItem value="standard">Standard</SelectItem>
          <SelectItem value="free">Free</SelectItem>
          <SelectItem value="banned">Banned</SelectItem>
        </SelectContent>
      </Select>
      <Select value={activityFilter} onValueChange={onActivityFilterChange}>
        <SelectTrigger id={`${idPrefix}-activity`} className="w-full sm:w-36">
          <SelectValue placeholder="Activity" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Users</SelectItem>
          <SelectItem value="active">Active (≤30d)</SelectItem>
          <SelectItem value="inactive">Inactive (&gt;30d)</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-10 bg-gray-200/60 dark:bg-gray-800 rounded animate-pulse" />
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 dark:bg-gray-800/60 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <Users className="h-6 w-6 text-gray-400" />
      </div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">No users match your filters</p>
      <p className="text-xs text-gray-500 mt-1">
        {hasActiveFilters ? 'Try clearing the filters above.' : 'Users will appear here once they sign up.'}
      </p>
      {hasActiveFilters && (
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={() => {
            onSearchChange('');
            onRoleFilterChange('all');
            onSubscriptionFilterChange('all');
            onActivityFilterChange('all');
          }}
        >
          <Filter className="h-3.5 w-3.5 mr-1.5" /> Clear filters
        </Button>
      )}
    </div>
  );

  // ─── Mobile Cards ──────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Mobile filter trigger */}
      <div className="flex items-center justify-between md:hidden">
        <span className="text-sm text-gray-500">Showing {users.length} of {totalCount.toLocaleString()}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          className="relative"
          aria-label="Show filters"
        >
          <Filter className="h-4 w-4 mr-1" /> Filters
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 block h-2.5 w-2.5 rounded-full bg-orange-500 ring-2 ring-background" />
          )}
        </Button>
      </div>

      {mobileFiltersOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border rounded-lg p-4 shadow-sm">
          <div className="flex flex-col gap-3">
            <Filters idPrefix="mobile" />
            <Button onClick={onExport} variant="outline" size="sm" className="w-full mt-1">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={() => setMobileFiltersOpen(false)} variant="ghost" size="sm" className="w-full">
              Close
            </Button>
          </div>
        </div>
      )}

      {/* Desktop toolbar */}
      <div className="hidden md:flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex flex-col md:flex-row md:items-center gap-2 flex-1">
          <Filters idPrefix="desktop" />
        </div>
        <Button onClick={onExport} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="text-sm text-gray-500">
        Showing {users.length} of {totalCount.toLocaleString()} users
      </div>

      {users.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block border rounded-lg overflow-hidden bg-white dark:bg-gray-900/40">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-gray-800">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Last Accessed</TableHead>
                  <TableHead>ChefCoins</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-12 text-right">Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decorated.map(({ u, tier, flag, countryName }) => (
                  <TableRow
                    key={u.id}
                    className="hover:bg-gray-50/70 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <TableCell><UserCell u={u} flag={flag} countryName={countryName} /></TableCell>
                    <TableCell>{getRoleBadge(u.role)}</TableCell>
                    <TableCell>{getSubscriptionBadge(u.subscription)}</TableCell>
                    <TableCell><LastAccessed u={u} tier={tier} /></TableCell>
                    <TableCell><ChefCoinCell u={u} /></TableCell>
                    <TableCell><UsageCell u={u} /></TableCell>
                    <TableCell><JoinedCell u={u} /></TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onManageUser(u)}
                        aria-label={`Manage ${u.email}`}
                        className="h-8 w-8 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {decorated.map(({ u, tier, flag, countryName }) => (
              <div
                key={u.id}
                className="border rounded-lg p-4 shadow-sm bg-white dark:bg-gray-900"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <UserCell u={u} flag={flag} countryName={countryName} />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onManageUser(u)}
                    aria-label={`Manage ${u.email}`}
                    className="h-8 w-8 shrink-0 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {getRoleBadge(u.role)}
                  {getSubscriptionBadge(u.subscription)}
                  <Badge variant="secondary" className={`inline-flex items-center gap-1 ${tier.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${tier.dot}${tier.pulse ? ' animate-pulse' : ''}`} />
                    {tier.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-xs">
                  <div className="col-span-2">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-0.5">
                      <Clock className="h-3 w-3" /> Last accessed
                    </div>
                    <div className={`font-medium ${tier.text}`}>
                      {formatDistanceToNow(u.lastActiveAt, { addSuffix: true })}
                      <span className="text-gray-400 font-normal"> · {formatShortDate(u.lastActiveAt)}</span>
                    </div>
                  </div>

                  <div className="col-span-2 border-t dark:border-gray-800 pt-2">
                    <div className="flex items-center gap-1.5 text-gray-500 mb-0.5">
                      <Coins className="h-3 w-3" /> ChefCoins
                    </div>
                    <div className="flex items-center gap-2 font-medium">
                      <span className="inline-flex items-center gap-1">
                        <ChefCoinIcon className="h-3 w-3 text-yellow-500" />{formatNumber(u.chefcoins_balance)}
                      </span>
                      <span className="text-emerald-600 dark:text-emerald-400">↑{formatNumber(u.coinsEarned)}</span>
                      <span className="text-rose-600 dark:text-rose-400">↓{formatNumber(u.coinsSpent)}</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">
                      {u.transactionsCount > 0
                        ? `${u.transactionsCount} txn · last ${formatDistanceToNow(u.lastTransactionAt!, { addSuffix: true })}`
                        : 'no transactions yet'}
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center gap-4 border-t dark:border-gray-800 pt-2">
                    <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-orange-500" />{formatNumber(u.studioUsesCount)} studio</span>
                    <span className="inline-flex items-center gap-1"><Gem className="h-3 w-3 text-purple-500" />{formatNumber(u.aiTokensUsed)} tokens</span>
                    <span className="inline-flex items-center gap-1"><FileText className="h-3 w-3 text-blue-500" />{formatNumber(u.recipesCreated)} recipes</span>
                  </div>

                  <div className="col-span-2 flex items-center gap-1.5 text-gray-500 border-t dark:border-gray-800 pt-2">
                    <Calendar className="h-3 w-3" />
                    Joined {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })} · {u.daysActive} days active
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">Page {currentPage} of {totalPages}</div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EnhancedUserTable;